import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useRosterLookup } from "../../../../hooks/useRosterLookup";
import { NotificationType, type OpenShiftRequest, type ReplaceShiftRequest } from "../../../../utils/models";
import { isPostedRequest } from "../../../../utils/openShiftMatching";
import { InlineHint } from "../../../UIToolkit/molecules/InlineHint";
import { approveDeclineCount } from "../gapsRequests";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** The request is still open and the person still asking. */
	isRequestLive: boolean;
	/** The approval is running. */
	isBusy: boolean;
	testIdPrefix: string;
};

/**
 * What approving does to everyone else, under the facts: who a replacement replaces, how many other people who asked
 * are declined — or, when nobody is, how many openings stay posted — and that the request has moved on.
 */
export const GapsApproveNotes: FC<Props> = ({ request, isRequestLive, isBusy, testIdPrefix }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const declineCount = approveDeclineCount(request);
	const stillNeededAfter = isPostedRequest(request) ? request.needed_nurses - 1 : 0;

	return (
		<Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
			{request.request_type === NotificationType.ReplaceShift && (
				<InlineHint tone="info" dataTestId={`${testIdPrefix}-replaces`}>
					{/* The request union is not discriminated by `request_type` in the model types; a replace_shift IS a ReplaceShiftRequest. */}
					{t("gaps.approve.replaces", { name: getNurseFormattedName((request as ReplaceShiftRequest).replacement_nurse_id) })}
				</InlineHint>
			)}
			{declineCount > 0 ? (
				<InlineHint tone="warning" dataTestId={`${testIdPrefix}-declines`}>
					{t("gaps.approve.declines", { count: declineCount })}
				</InlineHint>
			) : (
				stillNeededAfter > 0 && (
					<InlineHint tone="info" dataTestId={`${testIdPrefix}-still-needed`}>
						{t("gaps.approve.stillNeeded", { count: stillNeededAfter })}
					</InlineHint>
				)
			)}
			{/* Not while the approval runs: it closes the request itself, a beat before this sheet hands on. */}
			{!isRequestLive && !isBusy && (
				<InlineHint tone="warning" dataTestId={`${testIdPrefix}-gone`}>
					{t("gaps.approve.gone")}
				</InlineHint>
			)}
		</Box>
	);
};
